import React from 'react';
import { CalendarDaysIcon, CalendarRangeIcon, FileSpreadsheetIcon, FolderOpenIcon, HomeIcon, LayoutDashboardIcon, PartyPopperIcon, Settings2Icon, ShieldCheckIcon, UserRoundIcon, UsersIcon, WalletIcon, XIcon } from 'lucide-react';
import type { AppView } from '../../types/selfService';
import type { IconComponent } from '../../types/ui';
import { BrandLogo } from '../ui/BrandLogo';
export type NavSection = 'dashboard' | 'employees' | 'timeoff' | 'reports' | 'settings' | 'holidays' | 'schedule' | 'my-home' | 'my-timeoff' | 'my-info' | 'my-pay' | 'my-documents';
interface SidebarProps {
  view: AppView;
  active: NavSection;
  pendingCount: number;
  mobileOpen: boolean;
  onClose: () => void;
  onNavigate: (section: NavSection) => void;
}
type NavItem = {
  id: NavSection;
  label: string;
  icon: IconComponent;
  badge?: boolean;
};
const NAV: Record<AppView, {
  title: string;
  items: NavItem[];
}[]> = {
  admin: [{
    title: 'Workspace',
    items: [{
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboardIcon
    }, {
      id: 'employees',
      label: 'Employees',
      icon: UsersIcon
    }, {
      id: 'timeoff',
      label: 'Time Off',
      icon: CalendarDaysIcon,
      badge: true
    }, {
      id: 'reports',
      label: 'Reports',
      icon: FileSpreadsheetIcon
    }, {
      id: 'settings',
      label: 'Settings & Policies',
      icon: Settings2Icon
    }]
  }, {
    title: 'Company',
    items: [{
      id: 'holidays',
      label: 'Holidays',
      icon: PartyPopperIcon
    }, {
      id: 'schedule',
      label: 'Team Schedule',
      icon: CalendarRangeIcon
    }]
  }],
  employee: [{
    title: 'Me',
    items: [{
      id: 'my-home',
      label: 'My Home',
      icon: HomeIcon
    }, {
      id: 'my-timeoff',
      label: 'Time Off',
      icon: CalendarDaysIcon,
      badge: true
    }, {
      id: 'my-info',
      label: 'My Info',
      icon: UserRoundIcon
    }, {
      id: 'my-pay',
      label: 'My Pay',
      icon: WalletIcon
    }, {
      id: 'my-documents',
      label: 'My Documents',
      icon: FolderOpenIcon
    }]
  }, {
    title: 'Company',
    items: [{
      id: 'holidays',
      label: 'Holidays',
      icon: PartyPopperIcon
    }, {
      id: 'schedule',
      label: 'Team Schedule',
      icon: CalendarRangeIcon
    }]
  }]
};
export function Sidebar({
  view,
  active,
  pendingCount,
  mobileOpen,
  onClose,
  onNavigate
}: SidebarProps) {
  return <>
      {mobileOpen && <div className="fixed inset-0 z-30 bg-navy/40 backdrop-blur-sm lg:hidden" onClick={onClose} aria-hidden="true" data-id="element-170" data-mp="8aeipi9bfbyx" />}
      <aside className={`fixed inset-y-0 left-0 z-40 flex w-72 flex-col overflow-y-auto bg-navy px-5 pb-6 pt-6 transition-transform duration-300 lg:translate-x-0 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`} aria-label="Primary" data-id="element-171" data-mp="d26c00juvvsr">
        <button type="button" onClick={onClose} className="absolute right-3 top-3 rounded-lg p-1.5 text-white/60 hover:bg-white/10 hover:text-white lg:hidden" aria-label="Close navigation" data-id="element-172" data-mp="3li995cdce22u">
          <XIcon className="h-5 w-5" data-id="element-173" data-mp="36wln1lnkwnhc" />
        </button>

        <div data-id="element-174" data-mp="31w7msps7yhtv">
          <div className="flex h-16 items-center justify-center rounded-2xl bg-white px-5 shadow-lg shadow-black/20" data-id="element-175" data-mp="31wptkgciceg1">
            <BrandLogo src="https://madisonmortgage.com/clients/8165/uploads/2020/11/Madison-Mortgage-Logo-2.png" className="h-9 w-auto object-contain" wordmarkClassName="text-lg" data-id="element-176" data-mp="3fwiji9g7mqmy" />
          </div>
          <p className="mt-3 text-center text-[10px] font-semibold uppercase tracking-[0.28em] text-white/55" data-id="element-177" data-mp="3kuseok1xpr8k">{view === 'admin' ? 'HR Command Center' : 'Employee Self-Service'}</p>
        </div>

        <nav className="mt-10 flex-1 space-y-7" data-id="element-178" data-mp="3ujk16co87jc3">
          {NAV[view].map(group => <div key={group.title} data-id="element-179" data-mp="ce7bxkzwbudw">
              <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-white/35" data-id="element-180" data-mp="cf8ojtwk8oxq">{group.title}</p>
              <ul className="mt-3 space-y-1" data-id="element-181" data-mp="7gypv9bmn67d">
                {group.items.map(({
              id,
              label,
              icon: Icon,
              badge
            }) => {
              const isActive = id === active;
              return <li key={id} data-id="element-182" data-mp="26sunm8579s8r">
                      <button type="button" onClick={() => onNavigate(id)} aria-current={isActive ? 'page' : undefined} className={`group relative flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? 'bg-white/10 text-white' : 'text-white/65 hover:bg-white/[0.06] hover:text-white'}`} data-id="element-183" data-mp="2bldygxci699v">
                        {isActive && <span className="absolute -left-5 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-flame" aria-hidden="true" data-id="element-184" data-mp="1wwrn16pzy1nx" />}
                        <Icon className={`h-[18px] w-[18px] ${isActive ? 'text-azure' : 'text-white/50 group-hover:text-white/80'}`} aria-hidden="true" data-id="element-185" data-mp="33cbrk2is7vvv" />
                        <span className="flex-1 text-left" data-id="element-186" data-mp="3ig4pz4g3nxwg">{label}</span>
                        {badge && pendingCount > 0 && <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-flame px-1.5 text-[11px] font-semibold text-white" aria-label={`${pendingCount} pending`} data-id="element-187" data-mp="3dhtgwvxmprfa">
                            {pendingCount}
                          </span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>)}
        </nav>

        <div className="mt-8 rounded-2xl bg-white/[0.06] p-4 ring-1 ring-inset ring-white/10" data-id="element-188" data-mp="2psr1luqgwany">
          <div className="flex items-center gap-2 text-xs font-semibold text-white" data-id="element-189" data-mp="2lrp17aktdske">
            <ShieldCheckIcon className="h-4 w-4 text-azure" aria-hidden="true" data-id="element-190" data-mp="nyrpx89fnraq" />
            Sample data only
          </div>
          <p className="mt-1.5 text-[11px] leading-relaxed text-white/55" data-id="element-191" data-mp="2dgoq7ldvnhqi">All people, numbers and documents shown here are fictional demo records.</p>
        </div>

        <div className="mt-5 flex flex-col items-center gap-1.5" data-id="element-192" data-mp="2i8zqwqgfz7k4">
          <BrandLogo src="https://madisonmortgage.com/clients/8165/uploads/2020/04/footer-logo.png" className="h-7 w-auto object-contain opacity-70" wordmarkClassName="rounded-md bg-white px-2 py-0.5 text-xs opacity-70" data-id="element-193" data-mp="24gt9z25gswjm" />
          <p className="text-[10px] tracking-wide text-white/40" data-id="element-194" data-mp="316akbufe76rn">{view === 'admin' ? 'v1 · HR admins' : 'v1 · Employee self-service'}</p>
        </div>
      </aside>
    </>;
}